<template>
    <filter-panel
        :filterOptions="filterOptions"
        :collapsed="true"
        :model-value="filterState"
        @update:modelValue="onUpdateFilters"
    />
    <v-data-table
        :headers="requestHeaders"
        :items="filteredRequests"
        item-key="id"
        class="elevation-1"
        :items-per-page="25"
        :search="search"
    >
        <template v-slot:item.needByDate="{ item }">
            {{ getDateOnlyValue(item.needByDate) }}
        </template>
        <template v-slot:item.actions="{ item }">
            <v-row>
                <v-icon @click="viewRequest(item)">$eye</v-icon>
            </v-row>
        </template>
        <template v-slot:item.status="{ item }">
            <div v-if="editedItem?.id !== item.id">
                <span @click="startEdit(item)" class="editable-text">{{ item.status }}</span>
            </div>
            <div v-else>
                <v-autocomplete
                    v-model="editedItem.status"
                    :items="requestStatusOptions"
                    density="compact"
                    variant="outlined"
                    label="Edit Status"
                    class="mt-2"
                ></v-autocomplete>
                <v-btn class="mb-1" size="small" variant="text" color="blue" @click="updateRequestStatus(item)"
                    >Save</v-btn
                >
                <v-btn class="mb-1" size="small" variant="text" color="red" @click="cancelEdit"
                    >Cancel</v-btn
                >
            </div>
        </template>
    </v-data-table>
</template>

<script setup lang="ts">
import { buildFilterOptionsFromData, filterData } from "@/lib/filterUtils";
import { samplesAPI, SavedRequest } from "@/services/samples/samplesAPI";
import { ref, computed, watch } from "vue";
import FilterPanel from "../common/FilterPanel.vue";
import { getDateOnlyValue } from "@/lib/dateUtils";
import { useRouter } from "vue-router";

const router = useRouter();

const { requests, search } = defineProps<{
    requests: SavedRequest[];
    search: string;
}>();

const filterState = ref(<Record<string, string[]>>{});
const filterConfig = ref([
    { name: "requestProtocol", prop: "protocolName" },
    { name: "status", prop: "status" },
    { name: "sourceExperiment", prop: "experimentCode" },
]);

const filterOptions = ref({});

const requestRows = computed(() => {
    return requests.map(mapRequestToRow);
});

const filteredRequests = computed(() => {
    return filterData(
        requestRows.value,
        filterState.value,
        filterConfig.value
    ) as SavedRequest[];
});

const requestStatusOptions = ref(["draft", "canceled", "awaiting processing", "in progress", "completed"]);

const editedItem = ref<SavedRequest | null>(null);

watch(
    () => requests,
    (newRequests) => {
        filterOptions.value = buildFilterOptionsFromData(
            newRequests.map(mapRequestToRow),
            filterConfig.value
        );
    },
    { immediate: true }
);

const emit = defineEmits<{
    (e: "requestUpdated", item: SavedRequest): void;
}>();

const requestHeaders = ref([
    { title: "Request ID", value: "code", sortable: true },
    { title: "Source Experiment", value: "experimentCode", sortable: true },
    { title: "Source Sample Set", value: "sampleSetCode", sortable: true },
    { title: "Protocol", value: "protocol.name", sortable: true },
    { title: "Requested By", value: "requestedByEmail", sortable: true },
    { title: "Need By Date", value: "needByDate", sortable: true },
    { title: "Status", value: "status", sortable: true },
    { title: "Actions", value: "actions", sortable: false },
]);

function viewRequest(item: SavedRequest) {
    router.push({ path: `/requestsV2/${item.code}` });
}

function onUpdateFilters(newFilterState: Record<string, string[]>) {
    filterState.value = newFilterState;
}

function mapRequestToRow(request: SavedRequest) {
    return {
        ...request,
        sampleSetCode: request.sourceSampleSet?.code,
        protocolName: request.protocol ? request.protocol.name : "N/A",
    };
}

function startEdit(item: SavedRequest) {
    editedItem.value = { ...item };
}

async function updateRequestStatus(item: SavedRequest) {
    const updatedRequest = {
        experimentCode: item.experimentCode,
        sourceSampleSetId: item.sourceSampleSet!.id,
        status: editedItem.value!.status,
        needByDate: item.needByDate,
        requestedByEmail: item.requestedByEmail,
        protocolId: item.protocol!.id,
        processorId: item.processor!.id,
        requestContainerTypeId: item.requestContainerType?.id ?? null,
        samplesContainerTypeId: item.samplesContainerType?.id ?? null,
    }
    if (editedItem.value) {
        await samplesAPI.updateRequest(item.id, updatedRequest);
        editedItem.value = null;
    }
    emit("requestUpdated", item);
}

function cancelEdit() {
    editedItem.value = null;
}
</script>

<style scoped>
.editable-text {
    cursor: pointer;
    text-decoration: underline;
}
</style>
